import { throttle } from 'throttle-debounce'
import { DateTime as dateTime } from 'luxon'
import { getTimeZoneFromFullcalendar, getTimeFormatFromLocale } from './helpers'
import { getBookingSlot } from '~/common/components/calendar/event_store'

const AVAILABLE_HOVER_GROUP_ID = 'available-hover'
const AVAILABLE_ACTIVE_GROUP_ID = 'available-active'

export class DesktopBookingFlowManager {
  constructor(fullcalendar, durationInMinutesValue, showBookingForm) {
    this.fullcalendar = fullcalendar
    this.locale = fullcalendar.getOption('locale')
    this.durationInMinutesValue = durationInMinutesValue
    this.showBookingForm = showBookingForm
    this.hoverSlot = null
    this.activeSlot = null

    // Attach an event source that will show virtual events when hovering over an available slot & when clicking said slot
    this.attachBookingSlotEventSource()

    // Attach mousemove listener to calculate the currently availabe slot being hovered on, and if it's available update the event source so it shows a virtual event on that slot
    this.attachMouseMoveListener()

    // Attach click listener on a hover slot that when clicked will set the active slot - i.e it won't go away if you hover away and open the booking popup on the fullcalendar view
    this.attachHoverSlotClickListener()

    // Attach listener for when the active event mounts - we show a booking popup positioned relative to that active event.
    this.attachActiveSlotMountedListener()

    // Attach listener for when the booking form is hidden - so we can clear the active slot
    this.attachBookingFormHideListener()
  }

  attachBookingSlotEventSource() {
    this.eventSource = this.fullcalendar.addEventSource({
      id: 'booking-controller-event-source',
      events: (fetchInfo, successCallback) => {
        let events = []

        if (this.hoverSlot) {
          events.push({
            groupId: AVAILABLE_HOVER_GROUP_ID,
            className:
              'calendar-available-virtual-event calendar-available-hover',
            slotStart: this.hoverSlot,
          })
        }

        if (this.activeSlot) {
          events.push({
            groupId: AVAILABLE_ACTIVE_GROUP_ID,
            className:
              'calendar-available-virtual-event calendar-available-active',
            slotStart: this.activeSlot,
          })
        }

        events = events.map((event) => {
          const startUTC = event.slotStart.toUTC()
          const endUTC = startUTC.plus({
            minutes: this.durationInMinutesValue,
          })
          return {
            ...event,
            display: 'background',
            constraint: 'available',
            title: event.slotStart
              .setZone(getTimeZoneFromFullcalendar(this.fullcalendar))
              .toFormat(getTimeFormatFromLocale(this.locale)),
            id: `booking-manager-${startUTC.toISO()}`,
            start: startUTC.toISO(),
            end: endUTC.toISO(),
            extendedProps: { slotStart: event.slotStart },
          }
        })
        successCallback(events)
      },
    })
  }
  refetchEventSource() {
    this.eventSource.refetch()
  }

  attachMouseMoveListener() {
    // Yes, there doesn't seem to be a better way to do this.
    // Basically, we're attaching a mousemove listener to the fullcalendar element and then using document.elementsFromPoint to figure out what's being hovered on. We also don't set the hover slot if the mouse is over an unavailable slot.
    this.fullcalendar.el.addEventListener(
      'mousemove',
      throttle(60, (e) => {
        let elements = document.elementsFromPoint(e.clientX, e.clientY) || []
        let date, time, foundColFrame
        for (const el of elements) {
          if (el.classList.contains('calendar-unavailable')) {
            break
          } else if (el.classList.contains('fc-timegrid-slot-lane')) {
            time = el.dataset.time
          } else if (el.classList.contains('fc-day')) {
            date = el.dataset.date
          } else if (el.classList.contains('fc-timegrid-col-frame')) {
            foundColFrame = true
          }

          if (time && date && foundColFrame) {
            const [year, month, day] = date.split('-')
            const [hour, minute] = time.split(':')
            const hoverSlotStartsAt = dateTime.fromObject(
              {
                year,
                month,
                day,
                hour,
                minute,
              },
              { zone: getTimeZoneFromFullcalendar(this.fullcalendar) }
            )
            this.setHoverSlot(hoverSlotStartsAt)
            return
          }
          if (el === this.fullcalendar.el) {
            break
          }
        }
        this.setHoverSlot(null)
      })
    )
  }

  attachHoverSlotClickListener() {
    this.fullcalendar.el.addEventListener(
      'simplero:eventClick',
      ({ detail: { event } }) => {
        if (event.groupId === AVAILABLE_HOVER_GROUP_ID)
          this.setActiveSlot(event.extendedProps.slotStart)
      }
    )
  }

  attachActiveSlotMountedListener() {
    this.fullcalendar.el.addEventListener(
      'simplero:eventDidMount',
      ({ detail: { event, el } }) => {
        if (event.groupId !== AVAILABLE_ACTIVE_GROUP_ID) return

        this.activeSlotEventEl = el
        this.showBookingForm({
          slot: {
            start: this.activeSlot,
            end: this.activeSlot.plus({ minutes: this.durationInMinutesValue }),
          },
          type: 'tippy',
          getReferenceEl: () => this.activeSlotEventEl,
        })
      }
    )
  }

  attachBookingFormHideListener() {
    document.addEventListener('booking-form:hide', () =>
      this.setActiveSlot(null)
    )
  }

  setHoverSlot(slot) {
    if (!slot && !this.hoverSlot) return
    if (slot && this.hoverSlot?.equals(slot)) return
    if (slot) slot = getBookingSlot(slot, this.durationInMinutesValue)
    this.hoverSlot = slot
    this.refetchEventSource()
  }

  setActiveSlot(slot) {
    if (!slot && !this.activeSlot) return
    if (slot && this.activeSlot?.equals(slot)) return
    if (slot && this.hoverSlot?.equals(slot)) this.setHoverSlot(null)
    this.activeSlot = slot
    this.refetchEventSource()
  }
}
